import classnames from "classnames";
import {
  Dispatch,
  KeyboardEvent,
  MouseEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { OverlayPanel } from "primereact/overlaypanel";
import { Spacer } from "../../../components/Layout/Layout";
import {
  BaseFiltersResponse,
  FilterData,
} from "../../../core/api/common_table";
import useTranslations from "../../../core/i18n/useTranslations";
import {
  filterListToMap,
  mapToFilterList,
  validateFilterList,
} from "../../utils/customFilters";
import Button from "../Button/Button";
import Divider from "../Divider/Divider";
import Dropdown from "../Dropdown/Dropdown";
import { IDropdownItem } from "../Dropdown/DropdownItem";
import Icon from "../Icon";
import TextInput from "../Inputs/TextInput";
import { CustomFilterLinkedObjects } from "./CustomFilterLinkedObjects";
import {
  getFilterList,
  MoreState,
  toMoreState,
  useMoreFilter,
} from "../../../core/hooks/filters/useMoreFilter";
import { ClearFilter } from "./CleatFilters";
import { AutoDeactivatedAlarmsCheck } from "./AutoDeactivatedAlarmsCheck";
import { AutoDeactivatedFilter } from "../../../core/hooks/filters/useAutoDeactivatedFilter";
import { CustomAbsenceFilter } from "./CustomAbsenceFilter";
import useUser from "../../../core/user/useUser";
import { useFocusTrap } from "../../utils/useFocusTrap";
import { getAvailableOverlayHeight } from "../../utils/availableHeight";
import PrimeTooltip from "../PrimeTooltip/PrimeTooltip";
import { CustomActiveFilter } from "./CustomActiveFilter";
import Alert from "../Alerts/Alert";

import styles from "./Table.module.css";

// Filter of this type requires special handling
export const LINKED_OBJECT_ID = 8;
export const ABSENCE_ON_OBJECT = 9;
export const ACTIVE_OBJECT = 13;
export const NON_INLINE_FILTER_IDS = [
  LINKED_OBJECT_ID,
  ABSENCE_ON_OBJECT,
  ACTIVE_OBJECT,
];

export declare type CustomFiltersPopupActions = {
  open: () => void;
  close: () => void;
  toggle: () => void;
};

export interface Filter {
  columnId?: number;
  filterConditions?: IDropdownItem;
  value?: any;
  id: string;
  errors?: ("columnName" | "filterConditions" | "value")[];
}
export interface CustomFiltersProps {
  filterColumns: BaseFiltersResponse;
  filters?: FilterData[];
  notAvailableFilters: FilterData[];
  maxFiltersAmount?: number;
  applyFilters: (data: FilterData[]) => void;
  translate: (col: { keyName: string; name: string }) => string;
  tableState?: any;
  setUnsavedCustomFilters?: Dispatch<SetStateAction<MoreState>>;
  autoDeactivatedFilter?: AutoDeactivatedFilter;
  setAutoDeactivatedFilter?: (args: AutoDeactivatedFilter) => void;
  resetPageFilter: () => void;
  disableDropdowns?: boolean;
  disableFilters?: number[];
  extended?: boolean;
}

/** Popup that should appear when table rows seleted */
export const CustomFilters = ({
  filterColumns,
  filters,
  notAvailableFilters,
  maxFiltersAmount = 3,
  applyFilters,
  translate,
  tableState,
  setUnsavedCustomFilters,
  autoDeactivatedFilter,
  setAutoDeactivatedFilter,
  resetPageFilter,
  disableDropdowns,
  disableFilters = [],
  extended = false,
}: CustomFiltersProps) => {
  const t = useTranslations();
  const { config } = useUser();
  const { setMoreFilter, resetMoreFilter } = useMoreFilter(tableState);
  const customFiltersOverlayRef = useRef<OverlayPanel>(null);

  const {
    containerRef: popupBodyRef,
    triggerRef: triggerButtonRef,
    onKeyDown: handleKeyDown,
    onShow: handlePopupShow,
    onHide: handlePopupHide,
  } = useFocusTrap(true);

  const customFiltersBlockRef = useRef<HTMLDivElement>(null);
  const [filtersMaxHeight, setFiltersMaxHeight] = useState<number>();

  const [filterItems, setFilterItems] = useState<Map<string, Filter>>(
    new Map(),
  );
  const [initialFilters, setInitialFilters] = useState<Map<string, Filter>>(
    new Map(),
  );
  const [newlyAddedFilterId, setNewlyAddedFilterId] = useState<string | null>(
    null,
  );
  const dropdownButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const userAbsencePermission = config?.show.includes("UserAbsence");

  useEffect(() => {
    let preDefinedFilters: Map<string, Filter> = new Map();
    if (filters) {
      preDefinedFilters = filterListToMap(filters);
    }
    if (tableState) {
      preDefinedFilters = filterListToMap(getFilterList(tableState));
    }
    setFilterItems(preDefinedFilters);
    setInitialFilters(preDefinedFilters);
    setUnsavedCustomFilters?.(toMoreState(mapToFilterList(preDefinedFilters)));
    applyFilters(mapToFilterList(preDefinedFilters));
  }, []);

  useEffect(() => {
    // Reset when filters are cleared
    if (!filters) {
      setFilterItems(new Map());
      setUnsavedCustomFilters?.({});
    }
  }, [filters]);

  useEffect(() => {
    if (!newlyAddedFilterId) return;

    const filterId = newlyAddedFilterId;
    const focusButton = (): boolean => {
      let button = dropdownButtonRefs.current.get(filterId);

      if (!button) {
        const container = document.getElementById(`dropdown-${filterId}`);
        button = container?.querySelector(
          'button[type="button"]',
        ) as HTMLButtonElement;
        if (button) {
          dropdownButtonRefs.current.set(filterId, button);
        }
      }

      if (button) {
        button.focus();
        return true;
      }
      return false;
    };

    requestAnimationFrame(() => {
      if (!focusButton()) {
        setTimeout(() => focusButton(), 50);
      }
      setNewlyAddedFilterId(null);
    });
  }, [newlyAddedFilterId]);

  const numInlineFilterItems = useMemo(
    () =>
      Array.from(filterItems).filter(
        ([_, item]) =>
          item.columnId && !NON_INLINE_FILTER_IDS.includes(+item.columnId),
      ).length,
    [filterItems],
  );

  const filterColumnsItems = useMemo(
    () =>
      filterColumns.map((col) => {
        const notAvailable = !!notAvailableFilters.find(
          (el) => col.id === el.id,
        );

        const alreadySelected = !!Array.from(filterItems).find(
          ([, item]) => item.columnId && col.id === +item.columnId,
        );

        const isInSelectedFilters = filters?.find((el) => col.id === el.id);

        return {
          name: translate(col),
          id: col.id,
          isSelected: false,
          disabled: (notAvailable && !isInSelectedFilters) || alreadySelected,
        };
      }),
    [filterColumns, notAvailableFilters, filterItems],
  );

  const getFilterValue = (columnId: number) =>
    mapToFilterList(filterItems).find(
      (filterItem) => filterItem.id && +filterItem.id === columnId,
    )?.value;

  const getFilterItem = (columnId: number, itemId: string): Filter => ({
    id: itemId,
    columnId: filterColumnsItems.find((i) => +i.id === columnId)?.id,
    value: getFilterValue(columnId),
  });

  const isFilterAvailable = (columnId: number) =>
    !!filterColumnsItems.some((i) => +i.id === columnId);

  const isFilterDisabled = (columnId: number) =>
    filterItems.size >= maxFiltersAmount && !getFilterValue(columnId);

  const onFilterChange = (columnId: number, changedItem: Filter) => {
    setFilterItems((filtersMap) => {
      const list = mapToFilterList(filtersMap).filter(
        (listItem) => +listItem.id !== columnId,
      );
      const newFiltersMap = filterListToMap(list);
      if (changedItem.value) {
        newFiltersMap.set(changedItem.id, changedItem);
      }
      setUnsavedCustomFilters?.(toMoreState(mapToFilterList(newFiltersMap)));
      return new Map(newFiltersMap);
    });
  };

  const showRows = () =>
    !!filterItems.size || autoDeactivatedFilter?.hideautodeactivated;

  const addFilter = useCallback(() => {
    const crypto = window.crypto;
    const array = new Uint32Array(1);
    const newId = crypto.getRandomValues(array)[0].toString();

    const newItem = {
      id: newId,
      columnId: filterColumnsItems.find((i) => !i.disabled)?.id,
    };

    filterItems.set(newId, newItem);
    setUnsavedCustomFilters?.(toMoreState(mapToFilterList(filterItems)));
    setNewlyAddedFilterId(newId);
    return setFilterItems(new Map(filterItems));
  }, [filterItems, filterColumnsItems]);

  const onApplyFilters = useCallback(() => {
    const { updatedFilters, hasErrors } = validateFilterList(filterItems);
    if (hasErrors) {
      setFilterItems(updatedFilters);
    } else {
      const filterList = mapToFilterList(filterItems);
      setInitialFilters(filterItems);
      setMoreFilter(filterList);
      applyFilters(filterList);
      setUnsavedCustomFilters?.(toMoreState(filterList));
      customFiltersOverlayRef.current?.hide();
      resetPageFilter();
    }
  }, [filterItems, applyFilters]);

  const onCancel = () => {
    setFilterItems(filterListToMap(filters || []));
    setUnsavedCustomFilters?.(toMoreState(filters || []));
    customFiltersOverlayRef.current?.hide();
  };

  const handleRemoveFilter = (item: Filter) => {
    const updatedFilterMap = new Map(filterItems);
    updatedFilterMap.delete(item.id);

    setFilterItems(updatedFilterMap);
    setUnsavedCustomFilters?.(toMoreState(mapToFilterList(updatedFilterMap)));
  };

  const handleTriggerClick = (e: MouseEvent<HTMLButtonElement>) => {
    triggerButtonRef.current = e.currentTarget;
    setFiltersMaxHeight(getAvailableOverlayHeight(e.currentTarget));
    customFiltersOverlayRef.current?.toggle(e);
  };

  const triggerButton = () => {
    const filterItemsSizeShifter = autoDeactivatedFilter?.hideautodeactivated
      ? 1
      : 0;
    const manyFilters = filterItems.size + filterItemsSizeShifter;

    return (
      <div className={styles.customFilterTrigger}>
        {showRows() ? (
          <Button
            text={`${filterItems.size + filterItemsSizeShifter} ${
              manyFilters > 1
                ? t("Common:custom_filters_many")
                : t("Common:custom_filters_single")
            }`}
            image="filter"
            variant="active"
            onClick={handleTriggerClick}
            dataCy="more_filters_btn"
          />
        ) : (
          <Button
            text={t("Common:custom_filters_more")}
            image="filter"
            variant="secondary"
            onClick={handleTriggerClick}
            dataCy="more_filters_btn"
          />
        )}
      </div>
    );
  };

  const renderFilterLine = (item: Filter) => {
    if (item.columnId) {
      if (NON_INLINE_FILTER_IDS.includes(+item.columnId)) {
        return null;
      }

      const items = filterColumnsItems.map((i) => ({
        ...i,
        isSelected: i.id === parseInt(String(item?.columnId ?? -1), 10),
      }));

      const selectedItem = items.find((i) => i.isSelected);

      const handleEnterPress = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter") {
          onApplyFilters();
        }
      };
      const uniqId = uuidv4();

      return (
        <div key={item.id} className={styles.filterRow}>
          <Dropdown
            title={t("Common:custom_filters_column_name")}
            id={`dropdown-${item.id}`}
            width={extended ? 250 : 200}
            items={filterColumnsItems
              .filter((i) => !NON_INLINE_FILTER_IDS.includes(i.id))
              .map((i) => ({
                ...i,
                isSelected: i.id === item?.columnId,
              }))}
            placeholder=""
            dropdownInPopup
            selectedItem={selectedItem}
            invalid={item.errors?.includes("columnName")}
            onSelectItem={({ id }) => {
              setFilterItems((filtersMap) => {
                item.columnId = id as number;
                item.filterConditions = undefined;
                filtersMap.set(item.id, item);
                setUnsavedCustomFilters?.(
                  toMoreState(mapToFilterList(filtersMap)),
                );
                return new Map(filtersMap);
              });
            }}
          />
          <TextInput
            required
            className={classnames(styles.filterValue, {
              [styles.filterValueExtended]: extended,
            })}
            label={t("Common:custom_filters_column_value")}
            value={item.value as string}
            invalid={item.errors?.includes("value")}
            onKeyDown={handleEnterPress}
            onChange={(e) => {
              setFilterItems((filtersMap) => {
                item.value = e.target.value;
                item.errors = undefined;
                filtersMap.set(item.id, item);
                setUnsavedCustomFilters?.(
                  toMoreState(mapToFilterList(filtersMap)),
                );
                return new Map(filtersMap);
              });
            }}
            validationError={
              item.errors?.includes("value")
                ? t("Errors:input_field_required")
                : undefined
            }
          />
          <button
            type="button"
            data-pr-at="right center-6"
            id={`customFilter-${uniqId}`}
            aria-label="Remove filter"
            className={styles.filterRemoveButton}
            onClick={() => handleRemoveFilter(item)}
            onKeyUp={(event: KeyboardEvent) => {
              if (event.key === " ") {
                handleRemoveFilter(item);
              }
            }}
            tabIndex={0}
          >
            <Icon name="x" />
          </button>
          <PrimeTooltip
            target={`#customFilter-${uniqId}`}
            position="right"
            content={t("Objects:remove_from")}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {triggerButton()}
      <OverlayPanel
        ref={customFiltersOverlayRef}
        className={classnames({
          customFilters: true,
        })}
        pt={{
          content: {
            className: styles.customFiltersOverlayContent,
          },
        }}
        onShow={handlePopupShow}
        onHide={handlePopupHide}
      >
        <section
          ref={popupBodyRef}
          className={styles.popupBody}
          data-cy="more_filters_popup"
          onKeyDown={handleKeyDown}
          aria-label={t("Common:custom_filters_more")}
        >
          <div
            ref={customFiltersBlockRef}
            className={classnames(styles.customFiltersBlock, {
              [styles.customFiltersBlockExtended]: extended,
            })}
            style={{ maxHeight: filtersMaxHeight }}
          >
            <div className={styles.customFiltersContent}>
              {filterItems.size >= maxFiltersAmount && (
                <>
                  <Alert
                    dataCy="max_filter_num_reached_alert"
                    variant="info"
                    icon="information-circle"
                    title={t("Objects:max_filter_num_reached")}
                    className={styles.maxFiltersAlert}
                  />
                  <Spacer size={16} />
                </>
              )}
              {isFilterAvailable(ACTIVE_OBJECT) && (
                <CustomActiveFilter
                  disabled={isFilterDisabled(ACTIVE_OBJECT)}
                  item={getFilterItem(ACTIVE_OBJECT, "active-objects")}
                  onChange={(item) => onFilterChange(ACTIVE_OBJECT, item)}
                />
              )}
              {userAbsencePermission &&
                isFilterAvailable(ABSENCE_ON_OBJECT) && (
                  <>
                    <Spacer size={16} />
                    <CustomAbsenceFilter
                      disabled={isFilterDisabled(ABSENCE_ON_OBJECT)}
                      item={getFilterItem(ABSENCE_ON_OBJECT, "object-absence")}
                      onChange={(item) =>
                        onFilterChange(ABSENCE_ON_OBJECT, item)
                      }
                    />
                  </>
                )}
              {isFilterAvailable(LINKED_OBJECT_ID) &&
                !disableFilters.includes(LINKED_OBJECT_ID) && (
                  <>
                    <Spacer size={16} />
                    <CustomFilterLinkedObjects
                      disabled={isFilterDisabled(LINKED_OBJECT_ID)}
                      item={getFilterItem(LINKED_OBJECT_ID, "linked-objects")}
                      onChange={(item) =>
                        onFilterChange(LINKED_OBJECT_ID, item)
                      }
                    />
                  </>
                )}

              {!disableDropdowns && (
                <>
                  <Spacer size={16} />
                  <Divider />
                  <Spacer size={16} />
                </>
              )}
              {autoDeactivatedFilter && setAutoDeactivatedFilter && (
                <>
                  <AutoDeactivatedAlarmsCheck
                    disabled={
                      filterItems.size >= maxFiltersAmount &&
                      !autoDeactivatedFilter.hideautodeactivated
                    }
                    autoDeactivatedFilter={autoDeactivatedFilter}
                    setAutoDeactivatedFilter={setAutoDeactivatedFilter}
                  />
                  <Spacer size={16} />
                  <Divider />
                  <Spacer size={16} />
                </>
              )}

              {numInlineFilterItems > 0 && (
                <>
                  {Array.from(filterItems).map(([_, item]) =>
                    renderFilterLine(item),
                  )}
                </>
              )}
              {maxFiltersAmount > filterItems.size && !disableDropdowns && (
                <Button
                  text={t("Common:custom_filters_add")}
                  variant="link"
                  iconVariant="primary"
                  image="plus"
                  onClick={addFilter}
                  className={styles.addFilterButton}
                  dataCy="add_more_filters_btn"
                />
              )}
            </div>
            <div className={styles.customFiltersFooter}>
              <Button
                variant="primary"
                onClick={onApplyFilters}
                text={t("Common:custom_filters_apply")}
                className={styles.applyFilterButton}
                disabled={
                  (filterItems.size === 0 &&
                    filterItems.size === initialFilters.size) ||
                  !!filterItems.entries().some((item) => !item[1].value)
                }
                dataCy="apply_more_filters_btn"
              />
              <Button
                variant="secondary"
                onClick={onCancel}
                text={t("Common:cancel")}
                dataCy="cancel_more_filters_btn"
              />
              <ClearFilter
                text={t("Common:labels_clear_filters")}
                onClearClick={() => {
                  resetMoreFilter();
                  setFilterItems(new Map());
                  setUnsavedCustomFilters?.({});
                  applyFilters(mapToFilterList(new Map()));
                }}
                filterToWatch={
                  (filters?.length || 0) > 0 ? mapToFilterList(filterItems) : {}
                }
                customStyles={{ float: "right" }}
              />
            </div>
          </div>
        </section>
      </OverlayPanel>
    </>
  );
};
